import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  BookOpen,
  Briefcase,
  Download,
  FileText,
  FolderOpen,
  Handshake,
  LogOut,
  PenLine,
  ScrollText,
  User,
} from "lucide-react";
import { applications, health, type HealthOut } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PetitionStepper } from "../components/PetitionStepper";
import { OnboardingProgress } from "../components/OnboardingProgress";

const STAGE_ROUTES: Record<string, string> = {
  profile: "/profile",
  evidence: "/publications",
  recommenders: "/recommenders",
  draft: "/applications",
  review: "/engagement-workspace",
};

export default function AppShell() {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const [healthInfo, setHealthInfo] = useState<HealthOut | null>(null);

  // Onboarding heuristic: a user with zero applications is still in
  // first-time setup. We collapse the sidebar nav (keeping only logo +
  // user footer) so the OnboardingProgress bar is the single source of
  // navigation — Hims.com-style one-task-per-screen feel. Once an
  // application exists, the full nav appears for dashboard-mode usage.
  const [appsCount, setAppsCount] = useState<number | null>(null);

  useEffect(() => {
    health.get().then(setHealthInfo).catch(() => undefined);
    applications
      .list()
      .then((apps) => setAppsCount(apps.length))
      .catch(() => setAppsCount(0));
  }, []);

  const isOnboarding = appsCount === 0;

  function handleStepClick(stageId: string) {
    const route = STAGE_ROUTES[stageId];
    if (route) navigate(route);
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="hidden lg:flex w-60 flex-col border-r border-slate-200 bg-white overflow-y-auto">
        {/* Logo */}
        <div className="border-b border-slate-100 px-4 py-4">
          <Link to="/applications" className="flex items-center gap-2.5">
            <div
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white"
              style={{ background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)" }}
            >
              <BookOpen className="h-3.5 w-3.5" />
            </div>
            <div className="min-w-0">
              <p className="font-display text-sm font-semibold tracking-tight text-slate-900">
                VisaGenius
              </p>
              <p className="truncate text-[11px] text-slate-500">
                {session?.tenantDisplayName ?? session?.tenantId}
              </p>
            </div>
          </Link>
        </div>

        {isOnboarding ? (
          /* Onboarding mode — slim sidebar. Navigation lives in the
             OnboardingProgress bar at the top of the main column so the
             user has exactly one place to look for "where do I go next?" */
          <div className="flex-1 px-4 py-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              First-time setup
            </p>
            <p className="mt-2 text-xs leading-relaxed text-slate-600">
              Use the progress bar at the top to move through the five steps.
              Your full dashboard appears once your first application is in
              place.
            </p>
          </div>
        ) : (
          <>
            {/* Petition Stepper */}
            <PetitionStepper onStepClick={handleStepClick} />

            {/* Nav grouped by stage */}
            <nav className="flex-1 space-y-4 px-2 py-4 overflow-y-auto">
              {NAV_GROUPS.map(({ stage, items }) => (
                <div key={stage}>
                  <h3 className="px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {stage}
                  </h3>
                  <div className="space-y-0.5">
                    {items.map(({ to, label, icon: Icon, end }) => (
                      <NavLink
                        key={to}
                        to={to}
                        end={end}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors ${
                            isActive
                              ? "bg-periwinkle-blue/10 font-medium text-periwinkle-blue"
                              : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <Icon
                              className={`h-4 w-4 shrink-0 ${isActive ? "text-periwinkle-blue" : "text-slate-400"}`}
                            />
                            {label}
                          </>
                        )}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
          </>
        )}

        {/* User footer */}
        <div className="border-t border-slate-100 px-3 py-3 space-y-2">
          <div className="flex items-center gap-2 px-2 py-2">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-periwinkle-blue/10 text-[11px] font-bold text-periwinkle-blue">
              {session?.userEmail?.[0]?.toUpperCase() ?? "?"}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-slate-900">{session?.userEmail?.split("@")[0]}</p>
              <p className="truncate text-[10px] text-slate-500">{session?.userEmail}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => { signOut(); navigate("/sign-in"); }}
            className="w-full flex items-center justify-start gap-2 px-2 py-2 rounded-md text-sm text-slate-600 hover:bg-rose-50 hover:text-rose-700 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      <main className="flex flex-1 flex-col overflow-y-auto">
        {healthInfo?.demo_mode && (
          <div className="flex items-center justify-between border-b border-amber-200 bg-amber-50 px-6 py-2 text-xs text-amber-900">
            <span>
              <strong>Demo mode</strong> — AI drafting is disabled; output is placeholder text.
            </span>
            <span className="text-[10px] font-medium uppercase tracking-wider text-amber-700">
              {healthInfo.environment}
            </span>
          </div>
        )}
        <OnboardingProgress />
        <Outlet />
      </main>
    </div>
  );
}

const NAV_GROUPS = [
  {
    stage: "My Cases",
    items: [
      { to: "/applications", label: "My Applications",    icon: FileText,  end: false },
    ],
  },
  {
    stage: "Profile",
    items: [
      { to: "/intake",      label: "Intake",             icon: Download,  end: false },
      { to: "/profile",     label: "Scholar Profile",    icon: User,      end: false },
    ],
  },
  {
    stage: "Evidence",
    items: [
      { to: "/publications",label: "Publications",       icon: BookOpen,  end: false },
    ],
  },
  {
    stage: "Petition",
    items: [
      { to: "/i140-intake",   label: "Form I-140 & I-907", icon: ScrollText, end: false },
      { to: "/letter-studio", label: "Letter Studio",    icon: PenLine,    end: false },
      { to: "/documents",     label: "Documents",        icon: FolderOpen, end: false },
    ],
  },
  {
    stage: "Attorney Network",
    items: [
      { to: "/marketplace", label: "Attorney Marketplace", icon: Briefcase, end: false },
      { to: "/engagements", label: "Engagements",        icon: Handshake, end: false },
    ],
  },
];
